import React, { useEffect, useMemo, useState } from "react";
import Header from "./../component/Header";
import Footer from "./../component/Footer";
import HeadingSkeleton from "./../component/HeadingSkeleton";
import { API_BASE_URL, IMAGE_URL } from "../utils/app_url";
import { useLanguage } from "../context/LanguageContext";
const eventImg = require("./../assets/img/event1.png");

const EVENT_CATEGORY_OPTIONS = [
  { value: "Order", labelKey: "eventcategoryorder" },
  { value: "Fair", labelKey: "eventcategoryfair" },
  { value: "Shows", labelKey: "eventcategoryshows" },
  { value: "Open Sunday", labelKey: "eventcategoryopensunday" },
  { value: "Open day", labelKey: "eventcategoryopenday" },
];

const EventListing = () => {
  const { selectedLanguage, langJson } = useLanguage();

  const [eventList, setEventList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [titleSearch, setTitleSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [expandedEventId, setExpandedEventId] = useState(null);

  const parseEventDate = eventItem => {
    const sourceDate =
      eventItem?.start_date ||
      eventItem?.event_start_date ||
      eventItem?.formatted_start_date ||
      eventItem?.created_at;

    if (!sourceDate) {
      return null;
    }

    const nativeParsed = new Date(sourceDate);
    if (!Number.isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }

    const parts = String(sourceDate).match(/(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
    if (!parts) {
      return null;
    }

    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[2], 10) - 1;
    const yearRaw = parseInt(parts[3], 10);
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const parsedDate = new Date(year, month, day);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const getEventCategory = eventItem => {
    return (
      eventItem?.category ||
      eventItem?.event_category ||
      eventItem?.category_name ||
      eventItem?.type ||
      ""
    );
  };

  const getEventDescription = eventItem => {
    return (
      eventItem?.description ||
      eventItem?.event_description ||
      eventItem?.details ||
      ""
    );
  };

  const toggleDescription = eventId => {
    setExpandedEventId(prev => (prev === eventId ? null : eventId));
  };

  const normalizeCategory = value => (value || "").trim().toLowerCase();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}frontend/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${user.authorisation.token}`,
        },
      });
      const responseData = await response.json();
      if (responseData.res === true) {
        setEventList(responseData?.data?.data || []);
      } else {
      }
    } catch (error) {
      console.error("Error during Sign UP:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedLanguage]);

  const uniqueDateLabels = useMemo(() => {
    const unique = new Set();
    eventList.forEach(item => {
      const value = (item?.filter_label || "").trim();
      if (value) {
        unique.add(value);
      }
    });

    const parseLabelDate = label => {
      const parsed = new Date(`01 ${label}`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    return Array.from(unique).sort((a, b) => {
      const dateA = parseLabelDate(a);
      const dateB = parseLabelDate(b);

      if (dateA && dateB) {
        return dateA - dateB;
      }

      if (dateA) return -1;
      if (dateB) return 1;

      return a.localeCompare(b);
    });
  }, [eventList]);

  const filteredAndSortedEvents = useMemo(() => {
    const now = new Date();
    const normalizedTitle = titleSearch.trim().toLowerCase();
    const normalizedLocation = locationSearch.trim().toLowerCase();

    const filtered = eventList.filter(item => {
      const itemTitle = (item?.title || "").toLowerCase();
      const itemLocation = (item?.location || "").toLowerCase();
      const itemCategory = normalizeCategory(getEventCategory(item));
      const itemDateLabel = (item?.filter_label || "").trim();

      const matchesTitle = !normalizedTitle || itemTitle.includes(normalizedTitle);
      const matchesLocation = !normalizedLocation || itemLocation.includes(normalizedLocation);
      const matchesCategory = categoryFilter === "all" || itemCategory === normalizeCategory(categoryFilter);
      const matchesDate = dateFilter === "all" || itemDateLabel === dateFilter;

      return matchesTitle && matchesLocation && matchesCategory && matchesDate;
    });

    return [...filtered].sort((a, b) => {
      const dateA = parseEventDate(a);
      const dateB = parseEventDate(b);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      const aIsUpcoming = dateA >= now;
      const bIsUpcoming = dateB >= now;

      if (aIsUpcoming && !bIsUpcoming) return -1;
      if (!aIsUpcoming && bIsUpcoming) return 1;

      if (aIsUpcoming && bIsUpcoming) {
        return dateA - dateB;
      }

      return dateB - dateA;
    });
  }, [categoryFilter, dateFilter, eventList, locationSearch, titleSearch]);

  return (
    <div>
      <Header />
      <style>{`body { background: #FFF; }`}</style>

      <div className="fullContainer">
        <div className="categoryDiv productSliderMain topNew tradePrdts eventListingModern">
          <div className="hdnSec">
            {loading ? (
              <HeadingSkeleton width="200px" height="28px" />
            ) : (
              <h2 className="storeTabHdn">{langJson?.events}</h2>
            )}
          </div>

          {!loading && (
            <div className="eventsFilterBar">
              <input
                type="text"
                placeholder={langJson?.title || "Title"}
                value={titleSearch}
                onChange={event => setTitleSearch(event.target.value)}
              />

              <input
                type="text"
                placeholder={langJson?.location || "Location"}
                value={locationSearch}
                onChange={event => setLocationSearch(event.target.value)}
              />

              <select value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)}>
                <option value="all">{langJson?.category || "Category"}</option>
                {EVENT_CATEGORY_OPTIONS.map(category => (
                  <option key={category.value} value={category.value}>
                    {langJson?.[category.labelKey] || category.value}
                  </option>
                ))}
              </select>

              <select value={dateFilter} onChange={event => setDateFilter(event.target.value)}>
                <option value="all">{langJson?.date || "Date"}</option>
                {uniqueDateLabels.map(label => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!loading && <div className="eventsFoundText">{filteredAndSortedEvents.length} {langJson?.events || "events"} found</div>}

          <div className="eventsTableWrap">
            <table className="eventsTable">
              <thead>
                <tr>
                  <th></th>
                  <th>{langJson?.date || "Date"}</th>
                  <th>{langJson?.title || "Title"}</th>
                  {/* <th>{langJson?.city || "City"}</th>
                  <th>{langJson?.country || "Country"}</th> */}
                  <th>{langJson?.location || "Location"}</th>
                  <th>{langJson?.category || "Category"}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  [...Array(6)].map((_, index) => (
                    <tr
                      key={`event-skeleton-${index}`}
                      className={index % 2 === 0 ? "eventMainRow eventRowOdd" : "eventMainRow eventRowEven"}
                    >
                      <td className="eventImageCell">
                        <div className="skeleton" style={{ width: "90px", height: "70px", borderRadius: "4px", marginBottom: 0 }} />
                      </td>
                      <td>
                        <div className="skeleton" style={{ width: "120px", height: "18px", marginBottom: 0 }} />
                      </td>
                      <td>
                        <div className="skeleton" style={{ width: "280px", height: "18px", marginBottom: 0 }} />
                      </td>
                      <td>
                        <div className="skeleton" style={{ width: "110px", height: "18px", marginBottom: 0 }} />
                      </td>
                      <td>
                        <div className="skeleton" style={{ width: "90px", height: "18px", marginBottom: 0 }} />
                      </td>
                    </tr>
                  ))
                )}

                {!loading && filteredAndSortedEvents.length === 0 && (
                  <tr>
                    <td colSpan="5">No events found</td>
                  </tr>
                )}

                {!loading && filteredAndSortedEvents.map((item, index) => {
                  const isExpanded = expandedEventId === item?.id;
                  const description = getEventDescription(item);
                  const rowClassName = index % 2 === 0 ? "eventMainRow eventRowOdd" : "eventMainRow eventRowEven";

                  return (
                    <React.Fragment key={item?.id}>
                      <tr className={rowClassName}>
                        <td className="eventImageCell">
                          <img src={item?.image ? `${IMAGE_URL}${item.image}` : eventImg} alt={item?.title || "Event"} />
                        </td>
                        <td>{item?.formatted_date || `${item?.formatted_start_date || ""}${item?.formatted_end_date ? ` - ${item?.formatted_end_date}` : ""}` || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className="eventTitleBtn"
                            onClick={() => toggleDescription(item?.id)}
                          >
                            {item?.title || "-"}
                          </button>
                        </td>
                        <td>{item?.location || "-"}</td>
                        <td>{getEventCategory(item) || "-"}</td>
                      </tr>

                      <tr className={`eventDescriptionRow ${isExpanded ? "expanded" : "collapsed"}`}>
                        <td colSpan="5">
                          <div className="eventDescriptionContent">
                            {description || "-"}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventListing;
